<template>
  <div
    class="form-input-action"
    :class="{
      'is-invalid': state === false,
      disabled: $attrs.disabled === '',
      active: type !== 'password' && !editMode
    }"
  >
    <BFormGroup
      :disabled="$attrs.disabled"
      :label="label"
      :label-for="$attrs.id"
    >
      <BInputGroup
        :invalid-feedback="finalErrorMessage"
      >
        <BFormInput
          ref="input"
          :class="$attrs['input-class'] || ''"
          :value="value"
          :state="state"
          :type="dynamicType"
          :readonly="type !== 'password' && !editMode"
          :aria-label="label || $attrs.placeholder"
          :size="size"
          v-bind="$attrs"
          v-on="listeners"
        />

        <template v-slot:append>
          <button
            v-if="type === 'password'"
            type="button"
            class="btn"
            :disabled="$attrs.disabled"
            :class="`btn-${size}`"
            @click="togglePassword"
          >
            <SvgIcon name="UI/Visible" />
          </button>

          <button
            v-if="type !== 'password' && !editMode"
            type="button"
            class="btn"
            :disabled="$attrs.disabled"
            @click="enableEditMode"
          >
            {{ editText }}
          </button>
          <div
            v-if="type !== 'password' && editMode"
            class="btn-group"
          >
            <button
              type="button"
              class="btn"
              :disabled="$attrs.disabled"
              @click="cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn"
              :disabled="$attrs.disabled"
              @click="save"
            >
              Save
            </button>
          </div>
        </template>
      </BInputGroup>
      <div v-if="errorMessage" class="invalid-feedback d-block">

        {{ errorMessage}}
      </div>
    </BFormGroup>
  </div>
</template>

<script>
import {BFormGroup} from 'bootstrap-vue/src/components/form-group/form-group'
import {BInputGroup} from 'bootstrap-vue/src/components/input-group/input-group'
import {BFormInput} from 'bootstrap-vue/src/components/form-input/form-input'

export default {
  components: {
    BFormGroup,
    BInputGroup,
    BFormInput,
  },
  inheritAttrs: false,
  props: {
    /**
     * The current value of the textarea. Result will always be a string.
     */
    value: {
      type: [String, Number],
      default: undefined,
    },

    /**
     * The type of input to render. See the docs for supported types.
     */
    type: {
      type: String,
      default: 'text',
    },

    /**
     * Controls the validation state appearance of the component. 'true' for valid, 'false' or 'null' for no validation state
     */
    isInvalid: {
      type: Boolean,
      default: false,
    },

    /**
     * The text of the Edit Button.
     */
    editText: {
      type: String,
      default: 'Edit',
    },

    /**
     * Text to show when the form group has an invalid state
     */
    errorMessage: {
      type: String,
      default: undefined,
    },

    /**
     * The Edit Mode is never activated.
     */
    static: {
      type: Boolean,
      default: false,
    },

    /**
     * Text to place in the label/legend of the form group
     */
    label: {
      type: String,
      default: undefined,
    },

    /**
     * Text to show when the form input has exceed the characters limit
     */
    charactersLimitMessage: {
      type: String,
      default: undefined,
    },

    /**
     * Characters limit
     */
    charactersLimit: {
      type: [String, Number],
      default: undefined,
    },

    size: {
      type: String,
      default: undefined,
    },
  },

  data: () => ({
    dynamicType: 'text',
    editMode: false,
    intialValue: null,
  }),

  computed: {
    state () {
      const charactersLimit = this.charactersLimit | 0

      if (this.errorMessage && (this.errorMessage.length > 0)) {
        return false
      } else if (this.isInvalid) {
        return false
      } else if (charactersLimit > 0 && charactersLimit < this.valueLength) {
        return false
      } else {
        return null
      }
    },

    finalErrorMessage () {
      if (this.charactersLimitMessage && this.state === false) {
        return this.charactersLimitMessage
      } else {
        return this.errorMessage
      }
    },

    listeners () {
      const {save, ...listeners} = this.$listeners
      return listeners
    },
  },

  watch: {
    editMode (newVal, oldVal) {
      if (newVal && !oldVal) {
        this.$refs.input.focus()
      }
    },
  },

  created () {
    this.dynamicType = this.type
    this.intialValue = this.value
  },

  methods: {
    togglePassword () {
      if (this.dynamicType !== 'password') {
        this.dynamicType = 'password'
      } else {
        this.dynamicType = 'text'
      }
    },

    enableEditMode (e) {
      if (!this.static) {
        this.editMode = true
      }

      /**
       * Event triggered by user interaction. Emitted after the Edit Button is clecked.
       */
      this.$emit('edit-mode', e)
    },

    cancel () {
      this.editMode = false

      if (this.$listeners && this.$listeners.input) {
        this.$listeners.input(this.intialValue)
      }
    },

    save () {
      this.editMode = false

      /**
       * Event triggered by user interaction. Emitted after the save button is clicked
       */
      this.$emit('save', this.value)
    },
  },
}
</script>

<style lang="scss">


.form-input-action {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;

  &.is-invalid {
    .input-group-append {
      .btn {
        border-right: 1px solid #d9331a;
        border-top: 1px solid #d9331a;
        border-bottom: 1px solid #d9331a;
      }
    }
  }

  &:hover {
    .form-control,
    .form-control:focus {
      border-color: $gray-700 !important;
    }

    button {
      border-color: $gray-700;
      color: $gray-800;
    }

    .input-group-append {
      .btn {
        border-right: 1px solid #757575;
        border-top: 1px solid #757575;
        border-bottom: 1px solid #757575;
      }
    }
  }

  &.disabled {
    pointer-events: none;
  }

  &.active {
    .form-control[readonly] {
      color: inherit;
      background-color: inherit;

      ~ .input-group-append button {
        background-color: inherit;
      }
    }
  }

  .form-control {
    border-right: none !important;

    &:disabled {
      pointer-events: none;
    }

    &:hover:not(.is-invalid):not(:invalid) {
      border-color: transparent;
    }



    &:focus {
      border-color: $gray-200;
      border-width: 1px;
    }

    &[readonly] {
      ~ .input-group-append button {
        background-color: $gray-000;
      }
    }
  }

  .btn {
    font-family: $font-family-base;
    font-weight: normal;
    font-size: $font-size-sm;
    color: $gray-300;
    background-color: transparent;

    border: 1px solid $gray-200;
    border-left: none;

    padding-left: 1rem;
    padding-right: 1rem;

    letter-spacing: normal;

    border-top-right-radius: $btn-border-radius;
    border-bottom-right-radius: $btn-border-radius;

    min-width: 0;

    &:focus,
    &.focus {
      outline: 0;
      box-shadow: none !important;
    }

    // Disabled comes first so active can properly restyle
    &.disabled,
    &:disabled {
      color: $gray-300;
      background-color: $gray-100;
      border-color: $gray-200;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  .btn-group {
    button {
      &:first-child {
        color: $gray-800;
        padding-right: 12px;
        border-right: none;
        border-radius: 0;

        &:after {
          content: '';
          border: 0.5px solid $gray-200;
          height: 12px;
          position: absolute;
          bottom: 12px;
          margin-left: 32.5px;
        }
      }

      &:last-child {
        color: $cyan-700;
        padding-left: 12px;
      }
    }
  }
}
</style>
